
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Image as ImageIcon, X } from 'lucide-react';
import { useUploader } from '@/hooks/editor/useUploader';
import { useToast } from '@/hooks/use-toast';

interface HeaderImageUploadProps {
  headerImageUrl?: string;
  onHeaderImageChange: (url: string) => void;
}

const HeaderImageUpload: React.FC<HeaderImageUploadProps> = ({ 
  headerImageUrl, 
  onHeaderImageChange 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(headerImageUrl || '');
  const { uploadByFile } = useUploader();
  const { toast } = useToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const response = await uploadByFile(file);
      
      if (response.success) {
        const url = response.file.url;
        setImageUrl(url);
        onHeaderImageChange(url);
        toast({
          title: "Upload successful",
          description: "Header image has been uploaded and set",
        });
      } else {
        toast({
          title: "Upload failed",
          description: "Could not upload the image. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Upload failed",
        description: "An error occurred while uploading the image.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(e.target.value);
  };

  const saveImageUrl = () => {
    if (imageUrl) {
      onHeaderImageChange(imageUrl);
      toast({
        title: "Image URL saved",
        description: "Header image URL has been updated",
      });
    }
  };

  const removeImage = () => {
    setImageUrl('');
    onHeaderImageChange('');
    toast({
      title: "Image removed",
      description: "Header image has been removed",
    });
  };

  return (
    <Card className="border-border/40 shadow-sm overflow-hidden transition-all duration-200 hover:shadow hover:border-border/60">
      <CardContent className="p-5 space-y-4">
        <div className="flex justify-between items-center">
          <Label className="text-lg font-medium">Header Image</Label>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={isUploading}
              onClick={() => document.getElementById('header-image-upload')?.click()}
              className="transition-all hover:bg-accent/50"
            >
              <Upload className="h-4 w-4 mr-1" />
              Upload
            </Button>
            {imageUrl && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={removeImage}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
        
        <Input 
          id="header-image-upload"
          type="file" 
          accept="image/*" 
          className="hidden" 
          onChange={handleFileChange}
        />

        <div className="flex space-x-2">
          <Input
            placeholder="Or enter image URL here"
            value={imageUrl}
            onChange={handleUrlChange}
            className="focus:border-primary/40 focus:ring-1 focus:ring-primary/30"
          />
          <Button 
            variant="outline" 
            onClick={saveImageUrl}
            disabled={!imageUrl}
            className="transition-all hover:bg-accent/50"
          >
            Set URL
          </Button>
        </div>
        
        {imageUrl ? (
          <div className="relative mt-2 border rounded-lg overflow-hidden aspect-video bg-muted/30 shadow-inner">
            <img 
              src={imageUrl} 
              alt="Header" 
              className="w-full h-full object-cover transition-all hover:scale-105 duration-700"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/1200x675?text=Image+Not+Found';
              }}
            />
          </div>
        ) : (
          <div className="border rounded-lg p-8 flex flex-col items-center justify-center bg-muted/30 aspect-video">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No header image set</p>
            <p className="text-xs text-muted-foreground mt-1">
              Upload or enter a URL to add a header image to your article
            </p>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground italic">
          The header image will appear at the top of your article. 
          Recommended dimensions: 1200×675 pixels (16:9 ratio).
        </p>
      </CardContent>
    </Card>
  );
};

export default HeaderImageUpload;
